import { NO, UIDialogView, UINativeScrollView, UITextView, UIView, YES } from "uicore-ts"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHCheckbox } from "../../Custom components/RH/RHCheckbox"
import { RHColor } from "../../Custom components/RH/RHColor"
import { RHTextArea } from "../../Custom components/RH/RHTextArea"
import { RHButtons, RHDialogs } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHProcurementDetailsViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"

export class PartDecisionChangeDialogView extends UIDialogView {
    readonly contentView = new UINativeScrollView().configuredWithObject({
        backgroundColor: RHDialogs.dialogSurfaceBackgroundColor,
        style: { borderRadius: RHDialogs.dialogSurfaceBorderRadius, boxShadow: RHDialogs.dialogSurfaceBoxShadow }
    }).addedAsSubviewToView(this.view)

    readonly titleLabel = new UITextView(undefined, RHDialogs.askQuestionTitleTextViewType).configuredWithObject({
        text: "Muuda otsust", textColor: RHDialogs.askQuestionTitleTextColor,
        textAlignment: RHDialogs.confirmationQuestionHeadingTextAlignment
    }).addedAsSubviewToView(this.contentView)

    readonly explanationLabel = new UITextView().configuredWithObject({
        isSingleLine: NO,
        textColor: RHDialogs.askQuestionExplanationTextColor,
        text: "Varasem edukaks tunnistamise otsus muutub kehtetuks ja hindamine jätkub olemasolevate pakkumustega. " +
            "Pakkumuste tingimused ei muutu. Varasem otsus ja muutmise põhjus jäävad ajalukku."
    }).addedAsSubviewToView(this.contentView)

    readonly reasonTextArea = new RHTextArea().configuredWithObject({
        isRequired: YES, placeholderText: "Otsuse muutmise põhjus (kohustuslik)"
    }).addedAsSubviewToView(this.contentView)

    readonly winnerWithdrawalCheckbox = new RHCheckbox().configuredWithObject({
        titleLabel: { text: "Senine edukas pakkuja loobus", isSingleLine: NO }, selected: NO
    }).addedAsSubviewToView(this.contentView)

    readonly noContractCheckbox = new RHCheckbox().configuredWithObject({
        titleLabel: { text: "Kinnitan, et lepingut pole sõlmitud", isSingleLine: NO }, selected: NO
    }).addedAsSubviewToView(this.contentView)

    readonly noContractExplanationLabel = new UITextView().configuredWithObject({
        text: "Kinnitus hõlmab ka suulist kokkulepet ja tellimusega sõlmitud lepingut.",
        isSingleLine: NO, textColor: RHDialogs.askQuestionExplanationTextColor
    }).addedAsSubviewToView(this.contentView)

    readonly validationMessageLabel = new UITextView().configuredWithObject({
        textColor: RHColor.danger, isSingleLine: NO
    })

    readonly submitButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Muuda otsust" }, variant: RHButtons.exitUtilityVariant,
        contentPadding: RHDialogs.askQuestionFooterButtonContentPadding,
        controlEventTargetAccumulator: { PrimaryActionTriggered: () => { void this._confirmDecisionChange() } }
    }).addedAsSubviewToView(this.contentView)

    readonly cancelButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Loobu" }, variant: RHButtons.exitUtilityVariant,
        contentPadding: RHDialogs.askQuestionFooterButtonContentPadding,
        controlEventTargetAccumulator: { PrimaryActionTriggered: () => { this.dismiss() } }
    }).addedAsSubviewToView(this.contentView)

    readonly _bottomView = new UIView().addedAsSubviewToView(this.contentView)
    userDidConfirmDecisionChange?: (reason: string, winningOfferWasWithdrawn: boolean, confirmsNoContract: boolean) => Promise<void>

    override _focusManager = new RHFocusManager(() => [
        this.reasonTextArea, this.winnerWithdrawalCheckbox, this.noContractCheckbox, this.cancelButton, this.submitButton
    ])

    constructor() {
        super()
        this.view = this.contentView
        this.dismissesOnTapOutside = NO
    }

    override showInRootView(animated: boolean) {
        super.showInRootView(animated)
        RHPageFocusManager.shared.pushManagerAsNewLayer(this._focusManager)
    }

    override dismiss(animated?: boolean) {
        super.dismiss(animated)
        RHPageFocusManager.shared.popLayer()
    }

    override willAppear(animated?: boolean) {
        super.willAppear(animated)
        this.reasonTextArea.focus()
    }

    async _confirmDecisionChange() {
        const reason = this.reasonTextArea.text.trim()
        if (!reason || reason.length > 4000 || !this.noContractCheckbox.selected) {
            this.validationMessageLabel.text = "Lisa põhjus (kuni 4000 märki) ja kinnita, et lepingut ei ole sõlmitud."
            this.validationMessageLabel.addedAsSubviewToView(this.contentView)
            this.setNeedsLayoutUpToRootView()
            return
        }
        this.submitButton.enabled = NO
        this.cancelButton.enabled = NO
        try {
            await this.userDidConfirmDecisionChange?.(reason, this.winnerWithdrawalCheckbox.selected, this.noContractCheckbox.selected)
            this.dismiss()
        }
        catch (error) {
            this.validationMessageLabel.text = String(error)
            this.validationMessageLabel.addedAsSubviewToView(this.contentView)
            this.setNeedsLayoutUpToRootView()
        }
        finally {
            this.submitButton.enabled = YES
            this.cancelButton.enabled = YES
        }
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const dialogBounds = this.contentBounds
        const dialogWidth = Math.min(RHProcurementDetailsViewTokens.closureDialog.maxWidth,
            dialogBounds.width * RHProcurementDetailsViewTokens.closureDialog.widthViewportRatio)
        this.contentView.frame = dialogBounds.rectangleWithWidth(dialogWidth, 0.5).rectangleWithHeight(0)
        const padding = RHDialogs.askQuestionContentInset
        const bounds = this.contentView.contentBoundsWithInset(padding)
        bounds.rectangleWithHeight(this.titleLabel).assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(padding, this.explanationLabel).assignedAsFrameOfView(this.explanationLabel)
            .rectangleForNextRow(padding, RHDialogs.askQuestionTextAreaHeight).assignedAsFrameOfView(this.reasonTextArea)
            .rectangleForNextRow(padding, RHButtons.compactHeight).assignedAsFrameOfView(this.winnerWithdrawalCheckbox)
            .rectangleForNextRow(padding * 0.5, RHButtons.compactHeight).assignedAsFrameOfView(this.noContractCheckbox)
            .rectangleForNextRow(padding * 0.5, this.noContractExplanationLabel).assignedAsFrameOfView(this.noContractExplanationLabel)
            .IF(this.validationMessageLabel.superview === this.contentView)
            .rectangleForNextRow(padding * 0.5, this.validationMessageLabel).assignedAsFrameOfView(this.validationMessageLabel)
            .ENDIF()
            .rectangleForNextRow(padding, RHDialogs.askQuestionFooterButtonHeight)
            .distributeViewsAlongWidth([this.cancelButton, this.submitButton], undefined, RHDialogs.askQuestionFooterButtonGap)
            .rectangleForNextRow(0, padding).assignedAsFrameOfView(this._bottomView)
        const height = Math.min(this._bottomView.frame.max.y, dialogBounds.height - padding * 2)
        this.contentView.frame = dialogBounds.rectangleWithWidth(dialogWidth, 0.5).rectangleWithHeight(height, 0.5)
    }
}
